import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, first, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Availability } from '../../../../../models/availability';
import { CustomerProductPrice } from '../../../../../models/price';
import { Product, ProductDimension, ProductMultiDimension } from '../../../../../models/product';
import { AuthService } from '../../../../../services/auth/auth.service';
import { AvailabilityService } from '../../../../../services/availability/availability.service';
import { GungFlowService } from '../../../../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../../../../services/price/price.service';
import { BaseViewConfigService, ProductViewType } from '../../../../../services/base-view-config/base-view-config.service';
import { ProductListConfigService } from '../../../../../services/product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from '../../../../../services/product-list-v2-config/product-list-v2-config.service';
import { ProductService } from '../../../../../services/products/product.service';
import { GridViewProductCardV2 } from '../../../../../components/product-grid-view-v2/product-grid-view-v2.component';
import { DynamicPageEditorHelperService } from '../../../services/dynamic-page-editor-helper/dynamic-page-editor-helper.service';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';

interface ListItem {
  id: string;
  component: any;
  data: GridViewProductCardV2;
}

@Component({
  selector: 'lib-dynamic-page-dc-products-list',
  standalone: false,
  templateUrl: './dynamic-page-dc-products-list.component.html',
  styleUrl: './dynamic-page-dc-products-list.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DynamicPageDcProductsListComponent extends DynamicPageDcBaseComponent implements OnInit {
  protected productService = inject(ProductService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected productListConfigService = inject(ProductListConfigService);
  protected productListV2ConfigService = inject(ProductListV2ConfigService);
  protected priceService = inject(PriceService);
  protected availabilityService = inject(AvailabilityService);
  protected authService = inject(AuthService);
  protected gungFlowService = inject(GungFlowService);
  protected dynamicPageEditorHelperService = inject(DynamicPageEditorHelperService);
  protected productListConfig = inject(ProductListConfigService);

  listItems: ListItem[] = [];
  defaultViewComponent: any;
  defaultViewData: Product[] = [];
  products: Product[];
  loadingProducts = false;
  productToAdd: string;
  isSales = false;
  isAnonymous = true;
  includeAvailability = false;
  fields = {
    productsIds: []
  };

  private refresh$ = new Subject<void>();
  // null marks ids already requested that resolved to no product, so they are not refetched on every edit
  private listItemsByProductId = new Map<string, ListItem | null>();
  private productsById = new Map<string, Product | null>();

  search = (searchTerm$: Observable<string>) => {
    return searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        (this.products ?? [])
          .filter(product => {
            let hasHitAllTerms = true;
            const queryTerms = term.split(' ');
            const terms = this.productListConfig.getSearchTerms(product);
            queryTerms.forEach(queryTerm => {
              const locatedTerm = terms.find(searchTerm => {
                return !!searchTerm && searchTerm.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
              });

              hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
            });
            return hasHitAllTerms;
          })
          .slice(0, 10)
      )
    );
  };

  inputFormatter = (product: Product) => product.id;

  resultFormatter = (product: Product) => product.id + ' - ' + product.name;

  ngOnInit(): void {
    super.ngOnInit();

    if (this.mode === 'editor') {
      this.loadSearchableProducts();
    }

    this.authService.getRoles().pipe(first()).subscribe(roles => {
      this.isSales = roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
      this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
    });

    this.gungFlowService.getSelectedFlow().pipe(first()).subscribe(
      flow => { this.includeAvailability = flow.useAvailabilities || flow.requireAvailability; }
    );

    if (this.mode !== 'renderer') {
      return;
    }

    this.refresh$
      .pipe(
        switchMap(() => this.loadProductsInfo()),
        takeUntil(this.unsubscribe)
      )
      .subscribe();

    if (this.edit) {
      this.dynamicPageEditorHelperService.productIdsChanged
        .pipe(
          filter(changedElement => changedElement === this.element),
          takeUntil(this.unsubscribe)
        )
        .subscribe(() => this.getProductsInfo());
    }

    this.getProductsInfo();
  }

  private loadSearchableProducts() {
    this.loadingProducts = true;
    this.productListConfig.getItems().pipe(
      map(products => {
        return products.flatMap(p => {
          if (p.productType === "multiDimension") {
            const mdp = p as ProductMultiDimension;
            const primaryDimensions = mdp.primaryDimension?.length
              ? mdp.primaryDimension
              : [{ id: '', name: '' } as ProductDimension];
            return primaryDimensions.flatMap(pd =>
              mdp.secondaryDimension.map(sd => ({
                ...p,
                id: mdp.modelId + pd.id + sd.id,
                name: [p.name, pd.name, sd.name].filter(part => !!part).join(' - '),
                primaryDimension: pd,
                secondaryDimension: sd
              }))
            );
          }
          return [p];
        });
      }),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: ps => {
        this.products = ps;
        this.loadingProducts = false;
      },
      error: () => (this.loadingProducts = false)
    });
  }

  addProduct(product: Product) {
    this.element.fields.productsIds = [...this.element.fields.productsIds, product.id];
    this.notifyProductIdsChanged();
    setTimeout(() => this.productToAdd = '');
  }

  getProductsInfo() {
    this.refresh$.next();
  }

  private loadProductsInfo(): Observable<unknown> {
    const productIds: string[] = [...(this.element.fields.productsIds ?? [])];

    if (this.baseViewConfigService.productViewType !== ProductViewType.defaultView) {
      const missingIds = productIds.filter(id => !this.listItemsByProductId.has(id));
      const missingItems$ = missingIds.length > 0 ? this.loadListItems(missingIds) : of([]);
      return missingItems$.pipe(
        tap(missingItems => {
          const loadedById = new Map(missingItems.map(item => [item.id, item]));
          missingIds.forEach(id => this.listItemsByProductId.set(id, loadedById.get(id) ?? null));
          this.listItems = productIds.map(id => this.listItemsByProductId.get(id)).filter(item => !!item);
        }),
        catchError(error => this.logLoadError(error))
      );
    }

    this.defaultViewComponent = this.productListConfigService.getLayouts()[0].getListItemComponent();
    const missingIds = productIds.filter(id => !this.productsById.has(id));
    const missingProducts$ = missingIds.length > 0 ? this.productService.getProductsByIds(missingIds).pipe(first()) : of([]);
    return missingProducts$.pipe(
      tap(missingProducts => {
        const loadedById = new Map(missingProducts.map(product => [product.id, product]));
        missingIds.forEach(id => this.productsById.set(id, loadedById.get(id) ?? null));
        this.defaultViewData = productIds.map(id => this.productsById.get(id)).filter(product => !!product);
      }),
      catchError(error => this.logLoadError(error))
    );
  }

  private loadListItems(productIds: string[]): Observable<ListItem[]> {
    return this.productService.getProductsByIds(productIds).pipe(
      first(),
      switchMap(products => forkJoin({
        products: of(products),
        prices: this.priceService.getCurrentCustomerPrices(productIds).pipe(first()),
        availabilities: this.includeAvailability ? this.availabilityService.getAvailabilities(productIds).pipe(first()) : of([])
      })),
      map(({ products, prices, availabilities }) => {
        const productById = new Map(products.map(product => [product.id, product]));
        return productIds
          .map(id => {
            const product = productById.get(id);
            if (!product) return null;
            const price = prices.find(p => p && p.productId === id) as CustomerProductPrice;
            const availability = (availabilities as Availability[]).find(a => a && a.productId === id) as Availability;
            const itemData: GridViewProductCardV2 = {
              product,
              price,
              availability,
              isSales: this.isSales,
              isAnonymous: this.isAnonymous,
              includeAvailability: this.includeAvailability
            };
            return {
              id,
              component: this.productListV2ConfigService.getProductGridCardComponent(itemData),
              data: itemData
            };
          })
          .filter(item => !!item);
      })
    );
  }

  private logLoadError(error: unknown): Observable<null> {
    console.error('Failed to load dynamic page product list', error);
    return of(null);
  }

  deleteProduct(productId) {
    this.element.fields.productsIds = this.element.fields.productsIds.filter(id => id !== productId);
    this.notifyProductIdsChanged();
  }

  dropProduct(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.element.fields.productsIds, event.previousIndex, event.currentIndex);
    this.notifyProductIdsChanged();
  }

  private notifyProductIdsChanged() {
    this.dynamicPageEditorHelperService.productIdsChanged.next(this.element);
  }
}
